import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { MapPin, Fish, Sparkles, Map, Search, Compass, Smartphone } from "lucide-react";

const browseStates = [
  { name: "Pennsylvania", abbr: "PA", image: "https://images.unsplash.com/photo-1570127532654-5e39a9a4f1a3?w=200&q=80" },
  { name: "Montana", abbr: "MT", image: "https://images.unsplash.com/photo-1508193638397-1c4234db14d8?w=200&q=80" },
  { name: "Colorado", abbr: "CO", image: "https://images.unsplash.com/photo-1472396961693-142e6e269027?w=200&q=80" },
  { name: "New York", abbr: "NY", image: "https://images.unsplash.com/photo-1485871882310-4e95ad580622?w=200&q=80" },
  { name: "Oregon", abbr: "OR", image: "https://images.unsplash.com/photo-1543039625-14cbd3802e7d?w=200&q=80" },
  { name: "Wyoming", abbr: "WY", image: "https://images.unsplash.com/photo-1506748686214-e9df14d4d9d0?w=200&q=80" },
  { name: "Idaho", abbr: "ID", image: "https://images.unsplash.com/photo-1469854523086-cc02fe5d8800?w=200&q=80" },
  { name: "West Virginia", abbr: "WV", image: "https://images.unsplash.com/photo-1476514525535-07fb3b4ae5f1?w=200&q=80" },
];

const topDestinations = {
  west: [
    "Madison River, MT", "Deschutes River, OR", "Sacramento River, CA",
    "Yakima River, WA", "McKenzie River, OR", "Henry's Fork, ID"
  ],
  rockies: [
    "South Platte River, CO", "Green River, UT", "Snake River, WY",
    "Yellowstone River, MT", "Bighorn River, MT", "North Platte, WY"
  ],
  east: [
    "Penns Creek, PA", "Delaware River, NY", "Battenkill River, VT",
    "Little River, TN", "Savage River, MD", "Au Sable River, MI"
  ]
};

export function LandingStaticSections() {
  const navigate = useNavigate();

  return (
    <>
      {/* Get the App Card */}
      <section className="py-8 px-4 sm:px-6 bg-background">
        <div className="max-w-6xl mx-auto">
          <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-primary via-primary/90 to-primary/80">
            <div className="absolute inset-0 opacity-10">
              <svg className="w-full h-full" viewBox="0 0 100 100" preserveAspectRatio="none">
                <pattern id="grid" width="10" height="10" patternUnits="userSpaceOnUse">
                  <path d="M 10 0 L 0 0 0 10" fill="none" stroke="currentColor" strokeWidth="0.5"/>
                </pattern>
                <rect width="100%" height="100%" fill="url(#grid)" />
              </svg>
            </div>
            <div className="relative flex flex-col sm:flex-row items-center justify-between gap-6 p-6 sm:p-8">
              <div className="flex items-center gap-4 text-center sm:text-left">
                <div className="hidden sm:flex w-16 h-16 rounded-full bg-white/20 items-center justify-center flex-shrink-0">
                  <Smartphone className="w-8 h-8 text-white" />
                </div>
                <div>
                  <h3 className="text-xl sm:text-2xl font-serif font-semibold text-white mb-1">
                    Take DIY Fly Fishing to the water
                  </h3>
                  <p className="text-white/80 text-sm sm:text-base">
                    Save favorites, drop waypoints, keep a trip journal and use maps offline in the app
                  </p>
                </div>
              </div>
              <Button
                onClick={() => navigate('/app')}
                className="bg-white text-primary hover:bg-white/90 font-semibold px-6 py-2 rounded-full shadow-lg flex-shrink-0"
                data-testid="landing-get-app-btn"
              >
                <Smartphone className="w-4 h-4 mr-2" />
                Get the app
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 px-4 sm:px-6 bg-primary text-primary-foreground">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-center">
            <div>
              <div className="text-4xl sm:text-5xl font-bold mb-2">10K+</div>
              <div className="text-primary-foreground/80">fishing destinations</div>
              <p className="text-sm text-primary-foreground/60 mt-2">Discover hidden gems and legendary waters</p>
            </div>
            <div>
              <div className="text-4xl sm:text-5xl font-bold mb-2">50K+</div>
              <div className="text-primary-foreground/80">access points</div>
              <p className="text-sm text-primary-foreground/60 mt-2">Find boat ramps, trailheads, and fishing access</p>
            </div>
            <div>
              <div className="text-4xl sm:text-5xl font-bold mb-2">&infin;</div>
              <div className="text-primary-foreground/80">AI recommendations</div>
              <p className="text-sm text-primary-foreground/60 mt-2">Personalized guides for any location, any time</p>
            </div>
          </div>
        </div>
      </section>

      {/* Browse by State */}
      <section className="py-16 px-4 sm:px-6 bg-muted/30">
        <div className="max-w-6xl mx-auto">
          <h2 className="font-serif text-2xl sm:text-3xl text-foreground text-center mb-10">Browse by state</h2>
          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-4">
            {browseStates.map((state) => (
              <div key={state.abbr} onClick={() => navigate(`/destinations?state=${state.name}`)} className="group cursor-pointer text-center">
                <div className="aspect-square rounded-xl overflow-hidden mb-2 shadow-md group-hover:shadow-lg transition-shadow">
                  <img src={state.image} alt={state.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                </div>
                <p className="text-sm font-medium text-foreground group-hover:text-accent transition-colors">{state.name}</p>
              </div>
            ))}
          </div>
          <div className="text-center mt-8">
            <Button onClick={() => navigate('/destinations')} variant="outline" className="rounded-full">
              <Map className="w-4 h-4 mr-2" /> View all states
            </Button>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 px-4 sm:px-6 bg-background">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <p className="text-xs font-bold uppercase tracking-widest text-accent mb-4">Your fishing companion</p>
              <h2 className="font-serif text-3xl sm:text-4xl text-foreground mb-6">Plan the perfect fishing adventure</h2>
              <div className="space-y-6">
                {[
                  { icon: Search, title: "Search & discover", desc: "Find the best fishing spots with detailed information and community reviews." },
                  { icon: Sparkles, title: "AI-powered guides", desc: "Get personalized recommendations, hatch charts, and weather-based suggestions." },
                  { icon: Compass, title: "Navigate & explore", desc: "Access points, directions, and offline maps to help you find your way." },
                ].map(({ icon: Icon, title, desc }) => (
                  <div key={title} className="flex gap-4">
                    <div className="w-12 h-12 rounded-full bg-accent/10 flex items-center justify-center flex-shrink-0">
                      <Icon className="w-6 h-6 text-accent" />
                    </div>
                    <div>
                      <h3 className="font-medium text-foreground mb-1">{title}</h3>
                      <p className="text-sm text-muted-foreground">{desc}</p>
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-8">
                <Button onClick={() => navigate('/ai-guide')} className="bg-accent hover:bg-accent/90 text-accent-foreground rounded-full">
                  <Sparkles className="w-4 h-4 mr-2" /> Try AI Fishing Guide
                </Button>
              </div>
            </div>
            <div className="relative">
              <img src="https://images.unsplash.com/photo-1545450660-3378a7f3a364?w=600&q=85" alt="Fly fishing" className="rounded-2xl shadow-2xl" />
            </div>
          </div>
        </div>
      </section>

      {/* Top Destinations */}
      <section className="py-16 px-4 sm:px-6 bg-muted/30">
        <div className="max-w-6xl mx-auto">
          <h2 className="font-serif text-2xl sm:text-3xl text-foreground text-center mb-10">Adventure anywhere</h2>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              { title: "Western Rivers", data: topDestinations.west },
              { title: "Rocky Mountain Waters", data: topDestinations.rockies },
              { title: "Eastern Streams", data: topDestinations.east },
            ].map(({ title, data }) => (
              <div key={title}>
                <h3 className="font-medium text-foreground mb-4 flex items-center gap-2">
                  <Fish className="w-4 h-4 text-accent" /> {title}
                </h3>
                <ul className="space-y-2">
                  {data.map((dest) => (
                    <li key={dest}>
                      <button onClick={() => navigate(`/destinations?q=${encodeURIComponent(dest.split(',')[0])}`)} className="text-sm text-muted-foreground hover:text-accent transition-colors text-left">
                        {dest}
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 px-4 sm:px-6 bg-primary text-primary-foreground">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl mb-6">Ready to find your water?</h2>
          <p className="text-lg text-primary-foreground/80 mb-8">Start exploring premier fly fishing destinations and plan your next adventure.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button onClick={() => navigate('/destinations')} className="bg-accent hover:bg-accent/90 text-accent-foreground h-12 px-8 rounded-full text-base">
              <MapPin className="w-4 h-4 mr-2" /> Explore Destinations
            </Button>
            <Button onClick={() => navigate('/ai-guide')} variant="outline" className="border-primary-foreground/30 text-primary-foreground hover:bg-primary-foreground/10 h-12 px-8 rounded-full text-base">
              <Sparkles className="w-4 h-4 mr-2" /> AI Fishing Guide
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-4 sm:px-6 bg-background border-t border-border">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="font-serif text-xl text-foreground">DIY Fly Fishing Guide</div>
            <div className="flex items-center gap-6 text-sm">
              <button onClick={() => navigate('/privacy')} className="text-muted-foreground hover:text-foreground transition-colors">Privacy Policy</button>
              <button onClick={() => navigate('/terms')} className="text-muted-foreground hover:text-foreground transition-colors">Terms & Conditions</button>
              <button onClick={() => navigate('/app')} className="text-muted-foreground hover:text-foreground transition-colors" data-testid="footer-get-app">Get the app</button>
              <button onClick={() => navigate('/delete-account')} className="text-muted-foreground hover:text-foreground transition-colors" data-testid="footer-delete-account">Delete account</button>
            </div>
            <p className="text-sm text-muted-foreground">&copy; 2025 DIY Fly Fishing Guide. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </>
  );
}
